import { Box, Tab, Tabs, Text, styled } from '@ltvco/refresh-lib/theme';
import { type SyntheticEvent, useEffect, useState } from 'react';

import {
  CLASS_ACTION_REPORT_CARD_PAD_X,
  CLASS_ACTION_REPORT_SECTION_IDS,
} from '../shared/constants';
import { DesktopReportSection } from '../shared/DesktopReportSection';
import { BodyParagraph } from '../shared/reportTypography';

import type { DocketDetailDocumentsSection } from '../types';

const DocumentsTabsScrollArea = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(-CLASS_ACTION_REPORT_CARD_PAD_X),
  marginRight: theme.spacing(-CLASS_ACTION_REPORT_CARD_PAD_X),
  marginTop: theme.spacing(-1),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const DocumentsTabsStyled = styled(Tabs)(({ theme }) => ({
  minHeight: 42,
  paddingLeft: theme.spacing(CLASS_ACTION_REPORT_CARD_PAD_X),
  paddingRight: theme.spacing(CLASS_ACTION_REPORT_CARD_PAD_X),
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 2,
  },
}));

const DocumentsTab = styled(Tab)(({ theme }) => ({
  fontFamily: 'Roboto, sans-serif',
  fontSize: 14,
  fontWeight: theme.typography.fontWeightMedium ?? 500,
  lineHeight: '24px',
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  minHeight: 42,
  paddingTop: theme.spacing(1.125),
  paddingBottom: theme.spacing(1.125),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}));

function DocumentsTabs({
  docketsSummary,
  opinionsSummary,
}: {
  docketsSummary: string;
  opinionsSummary: string;
}) {
  const [value, setValue] = useState(() => (docketsSummary.trim() ? 0 : 1));

  useEffect(() => {
    setValue((prev) => {
      const hasDockets = Boolean(docketsSummary.trim());
      const hasOpinions = Boolean(opinionsSummary.trim());
      if (prev === 0 && !hasDockets && hasOpinions) {
        return 1;
      }
      if (prev === 1 && !hasOpinions && hasDockets) {
        return 0;
      }
      return prev;
    });
  }, [docketsSummary, opinionsSummary]);

  const handleChange = (_: SyntheticEvent, next: number) => {
    setValue(next);
  };

  return (
    <>
      <DocumentsTabsScrollArea>
        <DocumentsTabsStyled
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <DocumentsTab disableRipple label="Dockets" />
          <DocumentsTab disableRipple label="Opinions" />
        </DocumentsTabsStyled>
      </DocumentsTabsScrollArea>
      <Box sx={{ pt: 2.5 }}>
        {value === 0 ? (
          docketsSummary.trim() ? (
            <BodyParagraph>{docketsSummary}</BodyParagraph>
          ) : (
            <Text color="text.secondary" sx={{ fontSize: 15 }}>
              No dockets summary available.
            </Text>
          )
        ) : opinionsSummary.trim() ? (
          <BodyParagraph>{opinionsSummary}</BodyParagraph>
        ) : (
          <Text color="text.secondary" sx={{ fontSize: 15 }}>
            No opinions summary available.
          </Text>
        )}
      </Box>
    </>
  );
}

export function DocumentsSection({
  documents,
}: {
  documents: DocketDetailDocumentsSection;
}) {
  const dockets = documents.dockets_summary?.trim() ?? '';
  const opinions = documents.opinions_summary?.trim() ?? '';

  const hasContent = Boolean(dockets) || Boolean(opinions);

  if (!hasContent) {
    return null;
  }

  return (
    <DesktopReportSection
      title="Documents"
      sectionId={CLASS_ACTION_REPORT_SECTION_IDS.documents}
    >
      <DocumentsTabs docketsSummary={dockets} opinionsSummary={opinions} />
    </DesktopReportSection>
  );
}
